import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from '../../components/toast/CustomToast'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Modal from '../../components/common/Modal'
import Pagination from '../../components/common/Pagination'
import MonacoEditor from '../../components/common/MonacoEditor'
import HeadersConfig from '../../components/common/HeadersConfig'
import api, { apiCall } from '../../utils/api'
import { eventAPI, supplierAPI, setupAPI } from '../../utils/apiHelpers'

const SQLPage = () => {
    const { token, isAdmin } = useAuth();
    const [databases, setDatabases] = useState([]);
    const [selectedDb, setSelectedDb] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [schema, setSchema] = useState(null);
    const [loadingSchema, setLoadingSchema] = useState(false);
    const [sqlQuery, setSqlQuery] = useState('');
    const [results, setResults] = useState(null);
    const [isExecuting, setIsExecuting] = useState(false);
    const [error, setError] = useState(null);
    const editorRef = useRef(null);
    const [monacoReady, setMonacoReady] = useState(false);
    const [expandedTables, setExpandedTables] = useState(new Set());

    // Load databases on mount
    useEffect(() => {
        const loadDatabases = async () => {
            try {
                const response = await api.get('/sql-admin/databases');
                setDatabases(response.data);
                if (response.data.length > 0) {
                    setSelectedDb(response.data[0].id);
                }
            } catch (err) {
                if (err.response?.status === 403) {
                    setError('Privilégios de admin obrigatórios');
                    toast.error('Privilégios de admin obrigatórios');
                } else {
                    setError('Falha ao carregar bancos');
                    toast.error('Falha ao carregar bancos');
                }
            }
        };

        if (token) {
            loadDatabases();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize Monaco Editor
    useEffect(() => {
        if (!monacoReady && selectedDb && isConnected) {
            loadMonacoEditor();
        }
    }, [selectedDb, isConnected, monacoReady]);

    const loadMonacoEditor = async () => {
        try {
            // Load Monaco loader script
            if (!window.require) {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.45.0/min/vs/loader.js';
                script.onload = initMonaco;
                document.head.appendChild(script);
            } else {
                initMonaco();
            }
        } catch (err) {

            setError('Falha ao carregar Monaco Editor');
        }
    };

    const initMonaco = () => {
        if (!window.require) return;

        require.config({ paths: { 'vs': 'https://cdn.jsdelivr.net/npm/monaco-editor@0.45.0/min/vs' } });
        require(['vs/editor/editor.main'], () => {
            const container = document.getElementById('sql-editor');
            if (container && !editorRef.current) {
                editorRef.current = window.monaco.editor.create(container, {
                    value: sqlQuery,
                    language: 'sql',
                    theme: 'vs-dark',
                    automaticLayout: true,
                    minimap: { enabled: false },
                    fontSize: 13,
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    tabSize: 2,
                    wordWrap: 'on',
                    wordWrapColumn: 120,
                    formatOnPaste: true,
                    suggest: {
                        snippetsPreventQuickSuggestions: false,
                        showWords: true,
                        showFunctions: true,
                        localityBonus: true
                    }
                });

                // Registrar autocomplete customizado (apenas com tabelas do banco conectado)
                window.monaco.languages.registerCompletionItemProvider('sql', {
                    provideCompletionItems: (model, position) => {
                        const suggestions = [];

                        // Palavras reservadas SQL
                        const sqlKeywords = [
                            'SELECT', 'FROM', 'WHERE', 'JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN', 'OUTER JOIN',
                            'ON', 'AND', 'OR', 'NOT', 'IN', 'LIKE', 'BETWEEN', 'IS NULL', 'IS NOT NULL',
                            'GROUP BY', 'HAVING', 'ORDER BY', 'LIMIT', 'OFFSET', 'DISTINCT',
                            'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE',
                            'CREATE', 'TABLE', 'DROP', 'ALTER', 'ADD', 'COLUMN',
                            'PRIMARY KEY', 'FOREIGN KEY', 'UNIQUE', 'DEFAULT', 'CHECK',
                            'CREATE INDEX', 'DROP INDEX',
                            'UNION', 'UNION ALL', 'INTERSECT', 'EXCEPT',
                            'AS', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
                            'SUM', 'AVG', 'COUNT', 'MIN', 'MAX',
                            'CAST', 'CONVERT', 'SUBSTR', 'LENGTH', 'UPPER', 'LOWER', 'TRIM',
                            'DATE', 'DATETIME', 'NOW', 'CURRENT_DATE', 'CURRENT_TIME'
                        ];

                        // Adicionar palavras-chave SQL
                        sqlKeywords.forEach(keyword => {
                            suggestions.push({
                                label: keyword,
                                kind: window.monaco.languages.CompletionItemKind.Keyword,
                                insertText: keyword,
                                sortText: '0_' + keyword,
                                detail: 'Palavra-chave SQL'
                            });
                        });

                        // Adicionar APENAS nomes de tabelas e colunas do banco CONECTADO
                        if (isConnected && schema && schema.tables) {
                            schema.tables.forEach(table => {
                                suggestions.push({
                                    label: table.name,
                                    kind: window.monaco.languages.CompletionItemKind.Struct,
                                    insertText: table.name,
                                    sortText: '1_' + table.name,
                                    detail: `Tabela (${table.columns.length} colunas)`
                                });

                                // Adicionar nomes de colunas com prefixo da tabela
                                table.columns.forEach(column => {
                                    const columnLabel = column.name;
                                    suggestions.push({
                                        label: columnLabel,
                                        kind: window.monaco.languages.CompletionItemKind.Field,
                                        insertText: columnLabel,
                                        sortText: '2_' + table.name + '.' + columnLabel,
                                        detail: `Coluna de ${table.name} (${column.type})`
                                    });
                                });
                            });
                        }

                        return { suggestions: suggestions };
                    }
                });

                setMonacoReady(true);
            }
        });
    };

    const handleConnect = async () => {
        if (!selectedDb) {
            toast.error('Selecione um banco de dados');
            return;
        }
        setError(null);

        try {
            // Step 1: Quick connection test (fast)
            const response = await api.get(`/sql-admin/databases/${selectedDb}/test-connection`);

            if (response.data.success) {
                setIsConnected(true);
                setExpandedTables(new Set());
                toast.success('Conectado ao banco de dados');

                // Step 2: Load schema in parallel (slower, but doesn't block)
                loadSchemaInBackground();
            }
        } catch (err) {
            console.log(err);
            const errMsg = err.response?.data?.detail || 'Conexão falhou';
            setError(errMsg);
            toast.error('Falha ao conectar ao banco de dados');
        }
    };

    const loadSchemaInBackground = async () => {
        // Check sessionStorage cache first
        const cacheKey = `schema_${selectedDb}`;
        const cachedSchema = sessionStorage.getItem(cacheKey);

        if (cachedSchema) {
            try {
                const parsed = JSON.parse(cachedSchema);
                const cacheAge = Date.now() - parsed.timestamp;

                // Use cache if less than 5 minutes old
                if (cacheAge < 300000) { // 5 minutes
                    setSchema(parsed.data);
                    setLoadingSchema(false);
                    console.log(`Schema loaded from cache (age: ${Math.round(cacheAge / 1000)}s)`);
                    return;
                }
            } catch (e) {
                console.error('Error parsing cached schema:', e);
            }
        }

        // Fetch from API if not cached or expired
        setLoadingSchema(true);
        try {
            // Use silentLoading: true to avoid showing global spinner
            const response = await api.get(`/sql-admin/databases/${selectedDb}/schema`, {
                silentLoading: true
            });
            setSchema(response.data);

            // Cache in sessionStorage
            sessionStorage.setItem(cacheKey, JSON.stringify({
                data: response.data,
                timestamp: Date.now()
            }));
        } catch (err) {
            console.error('Erro ao carregar schema:', err);
            toast.error('Erro ao carregar lista de tabelas', { autoClose: 2000 });
        } finally {
            setLoadingSchema(false);
        }
    };

    const executeQuery = async () => {
        // Check if connected first
        if (!isConnected) {
            toast.error('Conecte-se a um banco de dados primeiro');
            return;
        }

        if (!editorRef.current && !sqlQuery.trim()) {
            toast.error('Digite uma query SQL');
            return;
        }

        const query = editorRef.current ? editorRef.current.getValue() : sqlQuery;
        if (!query.trim()) {
            toast.error('Digite uma query SQL');
            return;
        }

        setIsExecuting(true);
        setError(null);

        try {
            const response = await api.post('/sql-admin/execute', {
                database_id: selectedDb,
                query: query
            });

            if (response.data.success) {
                setResults(response.data);
                toast.success(`Query executada: ${response.data.row_count} linhas afetadas`);
            } else {
                setError(response.data.error);
                toast.error(`Erro na query: ${response.data.error}`);
            }
        } catch (err) {
            const errMsg = err.response?.data?.error || 'Execução da query falhou';
            setError(errMsg);
            toast.error('Execução da query falhou');
        } finally {
            setIsExecuting(false);
        }
    };

    const getDbLabel = (db) => {
        if (db.type === 'main') return db.name;
        return `${db.name.replace('Empresa: ', '')}`;
    };

    const toggleTableExpanded = (tableName) => {
        setExpandedTables(prev => {
            const newSet = new Set(prev);
            if (newSet.has(tableName)) {
                newSet.delete(tableName);
            } else {
                newSet.add(tableName);
            }
            return newSet;
        });
    };

    const handleDisconnect = () => {
        setIsConnected(false);
        setSchema(null);
        setLoadingSchema(false);
        setResults(null);
        setError(null);
        setExpandedTables(new Set());
        setSqlQuery('');

        // Clear schema cache for this database
        if (selectedDb) {
            sessionStorage.removeItem(`schema_${selectedDb}`);
        }

        toast.success('Desconectado do banco de dados');
    };

    const downloadCSV = () => {
        if (!results || !results.data || results.data.length === 0) {
            toast.error('Nenhum dado para exportar');
            return;
        }

        try {
            const headers = results.columns || [];
            const rows = results.data;

            // Criar cabeçalho CSV
            const csvHeader = headers.map(h => `"${h}"`).join(',');

            // Criar linhas CSV
            const csvRows = rows.map(row => {
                return headers.map(header => {
                    const value = row[header];
                    // Escapar aspas duplas e envolver em aspas se necessário
                    if (value === null || value === undefined) {
                        return '""';
                    }
                    const stringValue = String(value).replace(/"/g, '""');
                    return `"${stringValue}"`;
                }).join(',');
            });

            // Combinar cabeçalho e linhas
            const csvContent = [csvHeader, ...csvRows].join('\n');

            // Criar blob e download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            // Usar nome do banco conectado ou timestamp
            const dbName = selectedDb === 'main' ? 'admin' : (databases.find(d => d.id === selectedDb)?.company_name || 'dados');
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `${dbName}_${timestamp}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            toast.success('Arquivo CSV baixado com sucesso');
        } catch (err) {

            toast.error('Erro ao exportar CSV');
        }
    };

    return (
        <div className="flex h-full bg-gray-50 dark:bg-gray-950">
            {/* Left Sidebar */}
            <div className="w-128 flex flex-col gap-4 p-4 overflow-hidden">
                {/* Database Control Section */}
                <div className="flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
                    <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Banco de Dados</h3>
                    </div>
                    <div className="p-4 flex-1 overflow-y-auto">
                        {isConnected && selectedDb && (
                            <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                <p className="text-xs font-medium text-blue-900 dark:text-blue-300">Conectado em:</p>
                                <p className="text-sm font-bold text-blue-700 dark:text-blue-400 truncate">
                                    {selectedDb === 'main'
                                        ? 'DB Admin'
                                        : databases.find(d => d.id === selectedDb)?.company_name || 'Empresa Desconhecida'
                                    }
                                </p>
                            </div>
                        )}

                        {/* Database Selector */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Selecionar Banco
                            </label>
                            <select
                                value={selectedDb || ''}
                                onChange={(e) => {
                                    setSelectedDb(e.target.value);
                                    setIsConnected(false);
                                    setSchema(null);
                                    setResults(null);
                                }}
                                disabled={isConnected}
                                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <option value="">Escolha um banco...</option>
                                {databases.map(db => (
                                    <option key={db.id} value={db.id}>
                                        {db.type === 'main'
                                            ? 'DB Admin'
                                            : db.company_name || 'Empresa Desconhecida'
                                        }
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Connect/Disconnect Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={handleConnect}
                                disabled={!selectedDb || isConnected}
                                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition flex items-center justify-center gap-2 text-sm"
                            >
                                {isConnected ? (
                                    <>
                                        <span className="material-icons sm">check_circle</span>
                                        Conectado
                                    </>
                                ) : (
                                    <>
                                        <span className="material-icons sm">link</span>
                                        Conectar
                                    </>
                                )}
                            </button>
                            {isConnected && (
                                <button
                                    onClick={handleDisconnect}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition flex items-center justify-center gap-2"
                                    title="Desconectar do banco de dados"
                                >
                                    <span className="material-icons sm">link_off</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Schema Tree Section */}
                {isConnected && (
                    <div className="flex-1 flex flex-col min-h-0 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
                        <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
                            <h4 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                Tabelas ({schema?.tables?.length || 0})
                                {loadingSchema && (
                                    <span className="animate-spin material-icons text-sm text-blue-500">refresh</span>
                                )}
                            </h4>
                        </div>
                        <div className="flex-1 overflow-y-auto p-3">
                            {loadingSchema && !schema ? (
                                <div className="flex items-center justify-center h-32">
                                    <div className="text-center text-gray-500 dark:text-gray-400">
                                        <div className="animate-spin material-icons text-3xl mb-2">refresh</div>
                                        <p className="text-sm">Carregando tabelas...</p>
                                    </div>
                                </div>
                            ) : schema && schema.tables && schema.tables.length > 0 ? (
                                <div className="space-y-2">
                                    {schema.tables.map((table) => (
                                        <div key={table.name} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                            <div
                                                className="bg-gray-100 dark:bg-gray-700 px-3 py-2 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition flex items-center gap-2"
                                                onClick={() => toggleTableExpanded(table.name)}
                                            >
                                                <span className="material-icons sm transition-transform" style={{transform: expandedTables.has(table.name) ? 'rotate(90deg)' : 'rotate(0deg)'}}>
                                                    chevron_right
                                                </span>
                                                <span className="material-icons sm">table_chart</span>
                                                <h5 className="font-medium text-gray-900 dark:text-white text-sm">
                                                    {table.name}
                                                </h5>
                                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                                                    {table.columns.length}
                                                </span>
                                            </div>
                                            {expandedTables.has(table.name) && (
                                                <div className="bg-gray-50 dark:bg-gray-800 max-h-48 overflow-y-auto">
                                                    {table.columns.map((col) => (
                                                        <div
                                                            key={col.name}
                                                            className="px-3 py-1 text-xs text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition"
                                                            title={`${col.name}: ${col.type}${col.primary_key ? ' (Chave Primária)' : ''}${!col.nullable ? ' (NÃO NULO)' : ''}`}
                                                        >
                                                            <span className="font-mono">
                                                                {col.name}
                                                            </span>
                                                            <span className="text-gray-500 dark:text-gray-400 text-xs ml-1">
                                                                {col.primary_key && '🔑 '}
                                                                {col.type}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : !loadingSchema && schema && schema.tables && schema.tables.length === 0 ? (
                                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                                    <span className="material-icons text-4xl mb-2">folder_off</span>
                                    <p className="text-sm">Nenhuma tabela encontrada</p>
                                </div>
                            ) : null}
                        </div>
                    </div>
                )}
            </div>

            {/* Right Panel - Editor & Results */}
            <div className="flex-1 flex flex-col overflow-hidden p-4 gap-4 min-h-0">
                {/* Editor Section */}
                <div className="flex-1 flex flex-col min-h-0 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900">
                    <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
                        <h3 className="text-gray-900 dark:text-white font-bold flex items-center gap-2">
                            <span className="material-icons sm">terminal</span>
                            Editor SQL
                        </h3>
                        <button
                            onClick={executeQuery}
                            disabled={!isConnected || isExecuting}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-500 disabled:cursor-not-allowed text-white rounded-lg font-medium transition flex items-center gap-2 flex-shrink-0 text-sm"
                        >
                            {isExecuting ? (
                                <>
                                    <span className="animate-spin material-icons sm">settings</span>
                                    Executando...
                                </>
                            ) : (
                                <>
                                    <span className="material-icons sm">play_arrow</span>
                                    Executar
                                </>
                            )}
                        </button>
                    </div>
                    <div id="sql-editor" className="flex-1 w-full overflow-hidden bg-gray-900"></div>
                </div>

                {/* Results Container */}
                <div className="flex-1 flex flex-col min-h-0 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-900 relative">
                    <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
                        <h3 className="text-gray-900 dark:text-white font-bold flex items-center gap-2">
                            <span className="material-icons sm">table_rows</span>
                            Resultados
                        </h3>
                        <button
                            onClick={downloadCSV}
                            disabled={!results || !results.data || results.data.length === 0}
                            title="Baixar resultados como CSV"
                            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent rounded transition"
                        >
                            <span className="material-icons sm">download</span>
                        </button>
                    </div>
                    <div className="flex-1 overflow-auto">
                    {isExecuting && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 rounded-lg">
                            <div className="text-center">
                                <div className="mb-4">
                                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 rounded-full">
                                        <span className="material-icons text-4xl text-white animate-spin">settings</span>
                                    </div>
                                </div>
                                <p className="text-white font-bold text-lg">Executando query...</p>
                                <p className="text-gray-300 text-sm mt-2">Aguarde um momento</p>
                            </div>
                        </div>
                    )}
                    {results ? (
                        <div className="p-4">
                            <div className="mb-4 flex items-center gap-4 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
                                <div>
                                    <p className="text-sm font-medium text-green-900 dark:text-green-300">Query executada com sucesso</p>
                                    <p className="text-xs text-green-700 dark:text-green-400">
                                        {results.row_count} linhas {results.data ? 'retornadas' : 'afetadas'} em {results.execution_time.toFixed(3)}s
                                    </p>
                                </div>
                            </div>

                            {results.data && results.data.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse text-sm">
                                        <thead>
                                            <tr className="bg-gray-100 dark:bg-gray-800">
                                                {results.columns && results.columns.map((col) => (
                                                    <th
                                                        key={col}
                                                        className="px-4 py-2 text-left font-bold text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700"
                                                    >
                                                        {col}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {results.data.map((row, idx) => (
                                                <tr
                                                    key={idx}
                                                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700"
                                                >
                                                    {results.columns && results.columns.map((col) => (
                                                        <td
                                                            key={`${idx}-${col}`}
                                                            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 font-mono text-xs break-words max-w-xs"
                                                        >
                                                            {String(row[col] ?? '').substring(0, 100)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : results.data ? (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    <span className="material-icons text-4xl mb-2">check_circle</span>
                                    <p>Query concluída. Nenhuma linha para exibir.</p>
                                </div>
                            ) : null}
                        </div>
                    ) : error ? (
                        <div className="p-4">
                            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                                <p className="text-sm font-bold text-red-900 dark:text-red-300 mb-2">Erro</p>
                                <p className="text-sm text-red-700 dark:text-red-400 font-mono break-words">
                                    {error}
                                </p>
                            </div>
                        </div>
                    ) : !isConnected ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-gray-500 dark:text-gray-400">
                                <span className="material-icons text-6xl mb-4">cloud_off</span>
                                <p className="text-lg font-medium">Não conectado</p>
                                <p className="text-sm">Selecione um banco e clique Conectar</p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-gray-500 dark:text-gray-400">
                                <span className="material-icons text-6xl mb-4 animate-pulse">query_builder</span>
                                <p className="text-lg font-medium">Pronto para executar</p>
                                <p className="text-sm">Escreva SQL e pressione Ctrl+Enter ou clique Executar</p>
                            </div>
                        </div>
                    )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============ Main App ============

export default SQLPage
